import React, { useState } from 'react';
import { FaCheck, FaSpinner, FaExclamationTriangle, FaEye, FaEyeSlash } from 'react-icons/fa';
import postService from '../services/postService';
import './PublishButton.css';

const PublishButton = ({
    post,
    onPublishSuccess,
    onPublishError,
    onStatusChange,
    className = '',
    showValidation = true,
    categoryId = null
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [validation, setValidation] = useState(null);
    const [showValidationModal, setShowValidationModal] = useState(false);

    const handlePublish = async () => {
        if (!post) {
            return;
        }

        if (post.is_published) {
            await handleUnpublish();
            return;
        }

        if (showValidation && validation && !validation.can_publish) {
            setShowValidationModal(true);
            return;
        }

        await performPublish();
    };

    const performPublish = async () => {
        setIsLoading(true);
        try {
            const result = await postService.publishPost(post.id, { categoryId });
            if (result.success) {
                const updatedPost = { ...post, ...(result.post || result.page) };
                if (onPublishSuccess) {
                    onPublishSuccess(result, updatedPost);
                }
                if (onStatusChange) {
                    onStatusChange(updatedPost);
                }
            }
        } catch (error) {
            if (error.type === 'validation_error') {
                setValidation({
                    can_publish: false,
                    errors: error.validationErrors || [],
                    warnings: error.validationWarnings || []
                });
                setShowValidationModal(true);
            } else if (onPublishError) {
                onPublishError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleUnpublish = async () => {
        setIsLoading(true);
        try {
            const result = await postService.unpublishPost(post.id);
            if (result.success) {
                const updatedPost = { ...post, ...(result.post || result.page) };
                if (onStatusChange) {
                    onStatusChange(updatedPost);
                }
            }
        } catch (error) {
            if (onPublishError) {
                onPublishError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleForcePublish = async () => {
        setShowValidationModal(false);
        await performPublish();
    };

    const buttonClass = [
        'publish-button',
        className,
        post?.is_published ? 'unpublish' : 'publish',
        isLoading ? 'loading' : '',
        validation && !validation.can_publish && !post?.is_published ? 'disabled' : ''
    ].filter(Boolean).join(' ');

    const canPerformAction = !isLoading && (post?.is_published || !showValidation || !validation || validation.can_publish);

    return (
        <>
            <button
                onClick={handlePublish}
                disabled={!canPerformAction}
                className={buttonClass}
                title={post?.is_published ? 'Make post private' : 'Make post public'}
            >
                {isLoading ? <FaSpinner className="spinner" /> : (post?.is_published ? <FaEyeSlash /> : <FaEye />)}
                {isLoading ? (post?.is_published ? 'Unpublishing...' : 'Publishing...') : (post?.is_published ? 'Unpublish' : 'Publish')}
                {validation && !validation.can_publish && !post?.is_published && (
                    <FaExclamationTriangle className="warning-icon" />
                )}
            </button>

            {showValidationModal && validation && (
                <div className="validation-modal-overlay">
                    <div className="validation-modal">
                        <div className="validation-modal-header">
                            <h3>
                                <FaExclamationTriangle className="warning-icon" />
                                Post Validation
                            </h3>
                            <button className="close-button" onClick={() => setShowValidationModal(false)}>×</button>
                        </div>
                        <div className="validation-modal-content">
                            {validation.errors?.length > 0 && (
                                <div className="validation-section errors">
                                    <h4>❌ Required Issues:</h4>
                                    <ul>{validation.errors.map((item, index) => <li key={index} className="error-item">{item}</li>)}</ul>
                                </div>
                            )}
                            {validation.warnings?.length > 0 && (
                                <div className="validation-section warnings">
                                    <h4>⚠️ Recommendations:</h4>
                                    <ul>{validation.warnings.map((item, index) => <li key={index} className="warning-item">{item}</li>)}</ul>
                                </div>
                            )}
                        </div>
                        <div className="validation-modal-actions">
                            <button className="btn btn-secondary" onClick={() => setShowValidationModal(false)}>Cancel</button>
                            {validation.can_publish ? (
                                <button className="btn btn-primary" onClick={handleForcePublish} disabled={isLoading}>
                                    {isLoading ? <FaSpinner className="spinner" /> : <FaCheck />}
                                    Publish Anyway
                                </button>
                            ) : (
                                <button className="btn btn-outline" onClick={() => setShowValidationModal(false)}>Fix Issues First</button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PublishButton;